import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { AppleSignInError } from '@freelancer/auth';
import type { BackendAllErrorCodes } from '@freelancer/datastore';
import { FacebookSignInError } from '@freelancer/facebook';
import { ErrorResponseData } from '@freelancer/freelancer-http';
import { GoogleSignInError } from '@freelancer/google-sign-in';
import type { LoginError } from '@freelancer/login-signup';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ErrorCodeApi } from 'api-typings/errors/errors';

@Component({
  standalone: false,
  selector: `app-login-signup-errors`,
  template: `
    @if (response.errorCode !== ErrorCodeApi.AUTH_FORCE_RESET_PASSWORD) {
      <fl-banner-alert
        [type]="BannerAlertType.ERROR"
        [ngSwitch]="response.errorCode"
      >
        <ng-container
          *ngSwitchCase="ErrorCodeApi.AUTH_USER_MISSING"
          i18n="Login wrong username password message"
        >
          Incorrect email, username or password.
        </ng-container>
        <ng-container
          *ngSwitchCase="ErrorCodeApi.USERNAME_ALREADY_IN_USE"
          i18n="Signup username in use error message"
        >
          The username you have chosen is already in use. Please provide a
          different one or
          <fl-link
            flTrackingLabel="ErrorSwitchToLogin"
            (click)="switchToLogin.emit()"
            >log in instead</fl-link
          >.
        </ng-container>
        <ng-container
          *ngSwitchCase="ErrorCodeApi.EMAIL_ALREADY_IN_USE"
          i18n="Signup email already in use error message"
        >
          The email address you have provided is already in use. Please provide
          a different one or
          <fl-link
            flTrackingLabel="ErrorSwitchToLogin"
            (click)="switchToLogin.emit()"
            >log in instead</fl-link
          >.
        </ng-container>
        <ng-container
          *ngSwitchCase="ErrorCodeApi.PASSWORD_INVALID"
          i18n="Login wrong username password message"
        >
          The password you have chosen can't be used. Make sure it's different
          to your username and meets all the requirements, then try again.
        </ng-container>
        <ng-container
          *ngSwitchCase="ErrorCodeApi.AUTH_CAPTCHA_REQUIRED"
          i18n="Login wrong username password message"
        >
          Please complete the CAPTCHA to log in.
        </ng-container>
        <ng-container
          *ngSwitchCase="ErrorCodeApi.SIGNUP_CAPTCHA_INVALID"
          i18n="Invalid captcha message"
        >
          Invalid Captcha response. Please
          <fl-link
            flTrackingLabel="CaptchaForm-ResetCaptcha"
            (click)="resetCaptcha.emit()"
            >reset the captcha</fl-link
          >
          and try again.
        </ng-container>
        <ng-container
          *ngSwitchCase="ErrorCodeApi.FORBIDDEN"
          i18n="Signup forbidden error message."
        >
          <!-- FORBIDDEN = sanctioned region -->
          Unfortunately, our services are currently unavailable in your region.
          Please refer to
          <fl-link
            flTrackingLabel="DetailsForm-LoginClosedAccountSupportLink"
            [link]="'/support/general/restrictions-in-some-countries'"
            >this article</fl-link
          >
          for further details.
        </ng-container>
        <ng-container
          *ngSwitchCase="ErrorCodeApi.AUTH_ACCOUNT_CLOSED"
          i18n="Login wrong username password message"
        >
          This account has been closed. Please refer to
          <fl-link
            flTrackingLabel="DetailsForm-LoginClosedAccountSupportLink"
            [link]="'/support/profile/can-i-reopen-my-closed-account'"
            >this article</fl-link
          >
          for further details.
        </ng-container>
        <ng-container
          *ngSwitchCase="ErrorCodeApi.AUTH_ACCOUNT_SUSPENDED"
          i18n="Login wrong username password message"
        >
          This account has been suspended. Please visit
          <fl-link
            flTrackingLabel="DetailsForm-SuspendedAccountLink"
            [link]="'/users/usersuspended.php'"
            >this page</fl-link
          >
          for further details.
        </ng-container>
        <ng-container
          *ngSwitchCase="ErrorCodeApi.AUTH_2FA_BLOCKED"
          i18n="Login wrong username password message"
        >
          Your login has been blocked due to signing in from an unknown device.
          Check your email and enter the code to log in.
        </ng-container>
        <ng-container
          *ngSwitchCase="ErrorCodeApi.AUTH_PRELOGIN_TOKEN_EXPIRED"
          i18n="Error message when login 2FA expired"
        >
          Your login attempt has expired. Please log in again.
        </ng-container>
        <ng-container
          *ngSwitchCase="ErrorCodeApi.EMAIL_BLOCKED"
          i18n="Login/signup email blocked message"
        >
          Please sign up with a valid email address.
        </ng-container>
        <!-- error handling for facebook login / signup -->
        <ng-container
          *ngSwitchCase="ErrorCodeApi.AUTH_FACEBOOK_EMAIL_EXISTS"
          i18n="Login facebook failed message"
        >
          <!-- "again" because this error only shows up if the user goes back from FB link -->
          Your email address is already associated with a Freelancer account.
          Please log in with Facebook again and link your accounts.
        </ng-container>
        <ng-container
          *ngSwitchCase="ErrorCodeApi.USER_ALREADY_FACEBOOK_LINKED"
          i18n="Login facebook failed message"
        >
          Your Facebook account and Freelancer account are already linked.
          Please go back and try logging in with Facebook again.
        </ng-container>
        <ng-container
          *ngSwitchCase="FacebookSignInError.CANCELED"
          i18n="Login facebook canceled message"
        >
          Facebook login was canceled. Please try again.
        </ng-container>
        <ng-container
          *ngSwitchCase="FacebookSignInError.EXPIRED"
          i18n="Login facebook expired message"
        >
          Your Facebook session has expired. Please log into Facebook and try
          again.
        </ng-container>
        <ng-container
          *ngSwitchCase="FacebookSignInError.NOT_AUTHORIZED"
          i18n="Login facebook failed message"
        >
          Could not connect your Facebook account. Please try again.
        </ng-container>
        <ng-container
          *ngSwitchCase="ErrorCodeApi.FACEBOOK_EMAIL_NOT_FOUND"
          i18n="Login failed to get facebook user email message"
        >
          Could not get the email linked to your Facebook account. Please check
          app's permission in your Facebook settings and try again.
        </ng-container>
        <ng-container
          *ngSwitchCase="ErrorCodeApi.AUTH_OAUTH_FAILED"
          i18n="Login facebook failed message."
        >
          Could not connect to your external account. Please try again, or try
          logging out of the account and logging back in.
        </ng-container>
        <!-- nothing much we can do about the other facebook errors -->
        <!-- our backend returned something bad -->
        <ng-container
          *ngSwitchCase="ErrorCodeApi.AUTH_FACEBOOK_LINK_FAILED"
          [ngTemplateOutlet]="genericFacebookError"
        ></ng-container>
        <!-- even facebook doesn't know -->
        <ng-container
          *ngSwitchCase="FacebookSignInError.UNKNOWN"
          [ngTemplateOutlet]="genericFacebookError"
        ></ng-container>
        <ng-template #genericFacebookError>
          <fl-generic-error
            i18n="Facebook sign in generic error text"
            [error]="response"
          >
            We could not connect your Facebook account.
          </fl-generic-error>
        </ng-template>
        <ng-container
          *ngSwitchCase="AppleSignInError.CANCELED"
          i18n="Login apple canceled message"
        >
          <!-- TODO: Link to native permissions? -->
          Sign in attempt was canceled. Please try again or enable Sign in with
          Apple in your permissions settings.
        </ng-container>
        <ng-container
          *ngSwitchCase="ErrorCodeApi.AUTH_APPLE_EMAIL_IS_NULL"
          i18n="Login failed to get apple user email message"
        >
          Could not get the email linked to your Apple account. Please revoke
          app's access to Apple ID in settings and try again.
        </ng-container>
        <!-- nothing much we can do about the other apple errors -->
        <!-- our backend failed -->
        <ng-container
          *ngSwitchCase="ErrorCodeApi.AUTH_APPLE_LINK_FAILED"
          [ngTemplateOutlet]="genericAppleError"
        ></ng-container>
        <!-- invalid request (shouldn't happen) -->
        <ng-container
          *ngSwitchCase="AppleSignInError.INVALID"
          [ngTemplateOutlet]="genericAppleError"
        ></ng-container>
        <!-- not handled (also shouldn't happen?) -->
        <ng-container
          *ngSwitchCase="AppleSignInError.NOT_HANDLED"
          [ngTemplateOutlet]="genericAppleError"
        ></ng-container>
        <!-- even apple doesn't know -->
        <ng-container
          *ngSwitchCase="AppleSignInError.UNKNOWN"
          [ngTemplateOutlet]="genericAppleError"
        ></ng-container>
        <ng-template #genericAppleError>
          <fl-generic-error
            i18n="Apple sign in generic error text"
            [error]="response"
          >
            We could not connect your Apple ID.
          </fl-generic-error>
        </ng-template>
        <ng-container
          *ngSwitchCase="GoogleSignInError.CANCELED"
          i18n="Google sign in canceled message"
        >
          Google sign in was canceled. Please try again.
        </ng-container>
        <!-- Most of the errors fall under here due to lack of error handling in the google service -->
        <ng-container *ngSwitchCase="GoogleSignInError.UNKNOWN">
          <fl-generic-error
            i18n="Google sign in generic error text"
            [error]="response"
          >
            We could not connect your Google account.
          </fl-generic-error>
        </ng-container>
        <ng-container
          *ngSwitchCase="ErrorCodeApi.AUTH_DEVICE_TOKEN_INVALID"
          i18n="Login unrecoverable error"
        >
          Something went wrong and we couldn't fetch some critical info. Please
          check your network connection or try again later.
          @if (response.requestId) {
            If the issue persists, contact
            <fl-link
              flTrackingLabel="DetailsForm-ErrorSupportLink"
              [link]="'/support'"
              >support</fl-link
            >
            with request ID {{ response.requestId }}.
          }
        </ng-container>
        <fl-generic-error
          *ngSwitchDefault
          [error]="response"
        ></fl-generic-error>
      </fl-banner-alert>
    }
  `,
  styleUrls: ['./login-signup-errors.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginSignupErrorsComponent {
  ErrorCodeApi = ErrorCodeApi;
  AppleSignInError = AppleSignInError;
  FacebookSignInError = FacebookSignInError;
  GoogleSignInError = GoogleSignInError;

  BannerAlertType = BannerAlertType;

  @Input() response: ErrorResponseData<LoginError | BackendAllErrorCodes>;
  /**
   * Event which triggers off a user clicking on a "log in" link from a signup error
   *
   * eg. for `USERNAME_ALREADY_TAKEN`
   */
  @Output() switchToLogin = new EventEmitter<null>();
  /** Event which triggers off a user clicking a "reset" link in a CAPTCHA error */
  @Output() resetCaptcha = new EventEmitter<null>();
}
